<script setup>
import {ref, watch, defineProps, defineEmits, computed, getCurrentInstance} from 'vue';
import { RecipientType, DiscountType, Language } from '../types.js';
import { LinkIcon, XIcon, CheckIcon, CopyIcon, TelegramIcon, ViberIcon, MailIcon } from './Icons.js';


const props = defineProps({
	isOpen: Boolean,
	invoice: Object,
});

const emit = defineEmits(['close']);
const isLinkCopied = ref(false);
const isTextCopied = ref(false);
const editableMessage = ref('');
const instance = getCurrentInstance()

const getLocale = () => {
	const l = instance?.proxy?.$i18n?.locale
	return typeof l === 'string' ? l : l?.value
}

const messageLang = ref(getLocale() || 'uk')

const paymentLink = computed(() =>
	props.invoice
		? `https://pay.invoiceapp.com/pay/${props.invoice.id}?lang=${messageLang.value.toLowerCase()}`
		: ''
);

const telegramUrl = computed(() =>
	paymentLink.value
		? `https://t.me/share/url?url=${encodeURIComponent(paymentLink.value)}&text=${encodeURIComponent(editableMessage.value)}`
		: ''
)

const viberUrl = computed(() =>
	editableMessage.value
		? `viber://forward?text=${encodeURIComponent(editableMessage.value)}`
		: ''
)

const mailUrl = computed(() =>
	props.invoice
		? `mailto:test@test.com?subject=Test&body=Hello`
		: ''
);

const handleEmailClick = () => {
	if (!mailUrl.value) return;

	const opened = window.open(mailUrl.value);

	setTimeout(() => {
		if (!opened || opened.closed) {
			alert(
				'Email client is not configured on your device.\n\n' +
				'Please copy the message text and send it via your email manually.'
			);
		}
	}, 500);
};

const messageText = computed(() => {
	if (!props.invoice) return ''

	const invoice = props.invoice

	const recipientName =
		invoice.recipient?.type === RecipientType.INDIVIDUAL
			? `${invoice.recipient.person?.firstName || ''} ${invoice.recipient.person?.lastName || ''}`.trim()
			: invoice.recipient?.legal?.name || 'Customer'

	const template =
		messageLang.value === 'uk'
			? `Вітаємо, {recipient}!\n\nОсь ваш рахунок №{number} від {date}.\nСума до оплати: {amount} {currency}.\n\nПризначення платежу: {purpose}\n\nВи можете здійснити оплату за посиланням:\n{link}\n\nДякуємо!`
			: `Hello {recipient}!\n\nHere is your invoice {number} dated {date}.\nAmount due: {amount} {currency}.\n\nPurpose: {purpose}\n\nYou can pay via this link:\n{link}\n\nThank you!`

	return template
		.replace('{recipient}', recipientName)
		.replace('{number}', invoice.number)
		.replace('{date}', invoice.date)
		.replace('{amount}', invoice.amountDue.toFixed(2))
		.replace('{currency}', invoice.currency)
		.replace('{purpose}', invoice.paymentPurpose || 'Invoice Payment')
		.replace('{link}', paymentLink.value)
})

const copyLink = () => {
	if (!paymentLink.value) return;

	navigator.clipboard.writeText(paymentLink.value)
		.then(() => {
			isLinkCopied.value = true;
			setTimeout(() => (isLinkCopied.value = false), 2000);
		})
		.catch(() => {
			console.error("Clipboard copy failed!");
		});
};

const copyText = () => {
	if (!messageText.value) return;

	navigator.clipboard.writeText(editableMessage.value)
		.then(() => {
			isTextCopied.value = true;
			setTimeout(() => (isTextCopied.value = false), 2000);
		});
};

watch(() => props.isOpen, (open) => {
	if (open) {
		messageLang.value = (getLocale() || 'uk').toLowerCase()
	}
})

watch(messageText, (val) => {
	editableMessage.value = val
}, { immediate: true })
</script>


<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div class="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden m-4 max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
              <h3 class="font-semibold text-lg text-slate-900 flex items-center gap-2">
                  <LinkIcon class="w-5 h-5 text-blue-600" />
                  {{ $t('invoices.pay_modal_title') }}
              </h3>
              <button @click="emit('close')" class="text-slate-400 hover:text-slate-600"><XIcon class="w-5 h-5" /></button>
          </div>
          <div class="p-6 space-y-6">
              <div class="flex justify-center">
                  <div class="bg-slate-100 p-1 rounded-lg flex w-full sm:w-auto">
                      <button @click="messageLang = 'uk'" :class="`flex-1 sm:flex-none px-6 py-1.5 rounded-md text-sm font-medium transition-all ${messageLang === 'uk' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`">Українська</button>
                      <button @click="messageLang = 'en'" :class="`flex-1 sm:flex-none px-6 py-1.5 rounded-md text-sm font-medium transition-all ${messageLang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`">English</button>
                  </div>
              </div>
              <div class="space-y-2">
                   <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">{{ $t('invoices.pay_tab_link') }}</label>
                   <div class="flex gap-2">
                       <input readonly :value="paymentLink" class="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 font-mono" />
                       <button @click="copyLink" :class="`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm whitespace-nowrap ${isLinkCopied ? 'bg-green-100 text-green-700' : 'bg-blue-600 text-white hover:bg-blue-500'}`">
                           <component :is="isLinkCopied ? CheckIcon : CopyIcon" class="w-4 h-4" />
                           {{ isLinkCopied ? $t('invoices.pay_link_copied') : $t('invoices.pay_copy_text_btn') }}
                       </button>
                   </div>
              </div>
              <div class="space-y-2">
                  <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
										{{ $t('invoices.pay_tab_text') }}
									</label>
								<div class="relative">
									<textarea
										v-model="editableMessage"
										class="w-full h-40 p-4 pr-16 bg-slate-50 border border-slate-200 rounded-xl text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
									/>

									<button
										@click="copyText"
										class="absolute top-2 right-4.5 flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-all"
										:class="isTextCopied
											? 'bg-green-100 text-green-700'
											: 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'"
									>
										<component :is="isTextCopied ? CheckIcon : CopyIcon" class="w-3 h-3" />
										{{ isTextCopied ? $t('invoices.pay_text_copied') : $t('invoices.pay_send_via') }}
									</button>
								</div>
									<div class="space-y-3">
<!--									<h4 class="text-xs font-bold text-slate-500 uppercase tracking-wider">-->
<!--										{{ $t('invoices.pay_send_via') }}-->
<!--									</h4>-->

									<div class="grid grid-cols-3 gap-3">
										<a
											:href="telegramUrl"
											target="_blank"
											rel="noopener noreferrer"
											class="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all gap-2"
										>
											<div class="w-10 h-10 rounded-full bg-[#0088cc]/10 flex items-center justify-center">
												<TelegramIcon class="w-5 h-5 text-[#0088cc]" />
											</div>
											<span class="text-xs font-medium text-slate-600">Telegram</span>
										</a>

										<a
											:href="viberUrl"
											class="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 hover:border-purple-400 hover:bg-purple-50 transition-all gap-2"
										>
											<div class="w-10 h-10 rounded-full bg-[#7360f2]/10 flex items-center justify-center">
												<ViberIcon class="w-5 h-5 text-[#7360f2]" />
											</div>
											<span class="text-xs font-medium text-slate-600">Viber</span>
										</a>

										<button
											@click="handleEmailClick"
											class="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all gap-2"
										>
											<div class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
												<MailIcon class="w-5 h-5 text-slate-600" />
											</div>
											<span class="text-xs font-medium text-slate-600">Email</span>
										</button>
									</div>
								</div>
							</div>
          </div>
      </div>
  </div>
</template>
